import { Button } from "@9amhealth/shared";
import { NineTeam } from "@9amhealth/wcl/generated/modules";
import { useBlocNext } from "@blac/react";
import type { FC } from "react";
import { useCallback, useEffect } from "react";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import { getHeadingProps, useFocusHeading } from "src/lib/useFocusHeading";
import SanityCubit from "src/state/SanityCubit/SanityCubit";
import UserPreferencesCubit, {
  UserPreferenceKeys
} from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import { toast, useBloc } from "src/state/state";
import { EstablishCareStepSettings } from "src/types/sanitySchema";
import SignupCustomBloc from "src/ui/components/SignupCustomContent/state/SignupCustomBloc";
import Translate from "../Translate/Translate";

const EstablishCareStep: FC = () => {
  const [, { nextStep, showSaving, hideSaving }] =
    useBlocNext(SignupCustomBloc);
  const [, { fetchEstablishCareStepSettings }] = useBloc(SanityCubit, {
    create: () => new SanityCubit<EstablishCareStepSettings>()
  });
  const [, { updateUserPreferences }] = useBlocNext(UserPreferencesCubit);

  const { headingRef } = useFocusHeading();

  // Fetch list of users from establish care step document
  const careTeamMembers = fetchEstablishCareStepSettings()?.careTeamMembers;

  useEffect(() => {
    // Manually register the team component if it's not already registered
    if (!customElements.get("nine-team")) {
      NineTeam.define("nine-team");
    }
  }, []);

  const handleContinue = useCallback(async () => {
    showSaving({
      text: translate("savingIndicator.info", { context: "establishCare" })
    });
    try {
      await updateUserPreferences({
        [UserPreferenceKeys.establishCareCompleted]: true
      });
      void nextStep();
    } catch (error) {
      hideSaving();
      reportErrorSentry(error);
      toast.error("error.generic");
    }
  }, [showSaving, nextStep, hideSaving]);

  return (
    <div
      style={{
        display: "grid",
        alignItems: "center",
        height: "100%"
      }}
    >
      <div>
        <nine-team
          hide-buttons-mobile
          text-align-mobile="left"
          style={{
            "--button-background": "var(--color-cream)",
            "--page-max-width": "1100px",
            maxWidth: "100vw",
            margin: 0
          }}
        >
          <div slot="text">
            <h1 className="as-h2" ref={headingRef} {...getHeadingProps()}>
              <Translate msg="establishCare.title" />
            </h1>
            <p className="as-body">
              <Translate msg="establishCare.description.firstParagraph" />
            </p>
            <p className="as-body">
              <Translate msg="establishCare.description.secondParagraph" />
            </p>
          </div>

          <div slot="items">
            {careTeamMembers?.map((user) => (
              <nine-card
                variant="team"
                gradient="true"
                responsive
                key={user.name}
              >
                <img
                  slot="image"
                  src={user.image?.url}
                  alt={`${user.name}, ${user.description}`}
                />
                <h5 className="as-subhead strong" slot="title">
                  {user.name}
                </h5>
                <p className="as-little" slot="content">
                  {user.description}
                </p>
              </nine-card>
            ))}
          </div>
        </nine-team>

        <nine-spacer s="xxl" class="hide-below-switch"></nine-spacer>
        <div
          style={{
            minHeight: "unset",
            position: "relative",
            marginTop: "-2.5rem",
            zIndex: 2
          }}
        >
          <Button onPress={() => void handleContinue()} center>
            <Translate msg="continue" />
          </Button>
        </div>
        <nine-spacer s="xxl"></nine-spacer>
      </div>
    </div>
  );
};

export default EstablishCareStep;
